import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useCzechSuklRsAtcGroupsAutocomplete } from '~/libs/infrastructure/api/availability';
import { Input } from '~/libs/ui/atoms/Input';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { useClickOutside } from '~/libs/utils/hooks';

export interface SearchAutocompleteItem {
	id: string;
	label: string;
}

export interface SearchAutocompleteProps {
	selectedItems: SearchAutocompleteItem[];
	disabled?: boolean;
	onChange(items: SearchAutocompleteItem[]): void;
}

export function SearchAutocomplete({ selectedItems, disabled, onChange }: SearchAutocompleteProps) {
	const [filter, setFilter] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [inputResetKey, setInputResetKey] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const selectedIds = new Set(selectedItems.map((item) => item.id));

	const { data, isFetching } = useCzechSuklRsAtcGroupsAutocomplete(
		{ filter, perPage: 10 },
		{ query: { enabled: !disabled && filter.trim().length > 1 } }
	);

	useClickOutside(containerRef, () => setIsOpen(false));

	useEffect(() => {
		if (disabled) {
			setIsOpen(false);
			setFilter('');
		}
	}, [disabled]);

	const handleSelect = (item: SearchAutocompleteItem) => {
		if (!selectedIds.has(item.id)) {
			onChange([...selectedItems, item]);
		}
		setFilter('');
		setIsOpen(false);
		// Force the Input to remount with an empty value (it keeps its own internal,
		// debounced state and doesn't react to external value changes), then refocus it.
		setInputResetKey((key) => key + 1);
	};

	useEffect(() => {
		if (inputResetKey === 0) {
			return;
		}
		inputRef.current?.focus();
		inputRef.current?.setSelectionRange(0, 0);
	}, [inputResetKey]);

	const handleRemove = (id: string) => {
		onChange(selectedItems.filter((item) => item.id !== id));
	};

	const showDropdown = !disabled && isOpen && filter.trim().length > 1;
	const items = data?.items ?? [];

	return (
		<div className='relative' ref={containerRef}>
			<label className='block mb-1 text-labelsmall text-gray-500 leading-tight' htmlFor='availability-search'>
				Hledat ATC, účinnou látku, název LP nebo SÚKL kód
			</label>

			<Input
				id='availability-search'
				key={inputResetKey}
				ref={inputRef}
				placeholder='Zadejte hledaný výraz…'
				value={filter}
				disabled={disabled}
				onBlur={() => {}}
				onChange={(value) => {
					if (disabled) {
						return;
					}
					setFilter(value);
					setIsOpen(true);
				}}
			/>

			{disabled && <p className='mt-1 text-xs text-gray-400'>Nelze kombinovat s filtrem Referenční skupina (RS)</p>}

			{showDropdown && (
				<div className='absolute z-10 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-300 rounded shadow-lg'>
					{isFetching && (
						<div className='flex justify-center py-3'>
							<SpinningLoadingIcon className='text-gray-400' />
						</div>
					)}

					{!isFetching && items.length === 0 && <div className='px-3 py-2 text-sm text-gray-500'>Nic nenalezeno</div>}

					{!isFetching &&
						items.map((item) => (
							<button
								key={item.id}
								type='button'
								className={`block w-full text-left px-3 py-2 text-sm hover:bg-cBlue-50 ${
									selectedIds.has(item.id) ? 'bg-cBlue-50 text-cBlue-700' : 'text-gray-900'
								}`}
								onClick={() => handleSelect(item)}
							>
								{item.label}
							</button>
						))}
				</div>
			)}

			{selectedItems.length > 0 && (
				<div className='flex flex-wrap gap-2 mt-2'>
					{selectedItems.map((item) => (
						<span
							key={item.id}
							className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded bg-cBlue-100 text-cBlue-800 text-sm'
						>
							{item.label}
							<button
								type='button'
								className='text-cBlue-600 hover:text-cBlue-900'
								aria-label={`Odebrat ${item.label}`}
								onClick={() => handleRemove(item.id)}
							>
								<FaTimes className='w-3 h-3' />
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	);
}
